import { Injectable } from '@angular/core';

/**
 * Detección del navegador del cliente (infraestructura).
 *
 * Se usa para el guard preventivo de compatibilidad: por ahora solo se frena
 * Safari (desktop e iOS), recomendando Google Chrome. El resto de navegadores
 * se permiten.
 *
 * Nota: la detección es por user-agent/vendor (heurística estándar). En iOS,
 * "Chrome" (CriOS) usa WebKit por debajo, pero NO se clasifica como Safari para
 * no bloquear a quien ya eligió Chrome.
 */
@Injectable({ providedIn: 'root' })
export class BrowserEnvironmentService {
  private readonly userAgent =
    typeof navigator !== 'undefined' ? navigator.userAgent ?? '' : '';
  private readonly vendor =
    typeof navigator !== 'undefined' ? navigator.vendor ?? '' : '';

  /** Navegadores que incluyen el token "Safari" en su UA pero NO son Safari. */
  private readonly excludedTokens =
    /(chrome|chromium|crios|edg|edgios|opr|opera|firefox|fxios|samsungbrowser|ucbrowser)/i;

  /**
   * True si el navegador es Safari (desktop o iOS). Excluye navegadores que
   * también exponen el token "Safari" en su user-agent (Chrome, Edge, etc.).
   */
  isSafari(): boolean {
    const ua = this.userAgent;
    if (!ua) {
      return false;
    }
    const hasSafariToken = /safari/i.test(ua);
    const isExcluded = this.excludedTokens.test(ua);
    // El vendor de Apple refuerza la detección y reduce falsos positivos.
    const appleVendor = /apple/i.test(this.vendor);
    const applePlatform = /(iphone|ipad|ipod|macintosh|mac os x)/i.test(ua);
    return hasSafariToken && !isExcluded && (appleVendor || applePlatform);
  }

  /** True si el navegador es Google Chrome (incluye Chrome en iOS: CriOS). */
  isChrome(): boolean {
    const ua = this.userAgent;
    if (!ua) {
      return false;
    }
    const hasChromeToken = /(chrome|crios|chromium)/i.test(ua);
    const isOtherChromium = /(edg|edgios|opr|opera|samsungbrowser)/i.test(ua);
    return hasChromeToken && !isOtherChromium;
  }
}
